import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from "react";

/**
 * Toast Context for global toast notifications.
 * Supports success, error, warning, and info toast types.
 */
const ToastContext = createContext(null);

// Toast types
export const TOAST_TYPES = {
  SUCCESS: "success",
  ERROR: "error",
  WARNING: "warning",
  INFO: "info",
};

// Default duration in milliseconds
const DEFAULT_DURATION = 4000;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);

  // Add a toast
  const addToast = useCallback(
    (message, type = TOAST_TYPES.SUCCESS, duration = DEFAULT_DURATION) => {
      const id = ++toastIdRef.current;

      const newToast = {
        id,
        message,
        type,
        duration,
      };

      setToasts((prev) => [...prev, newToast]);

      // Auto-remove after duration
      if (duration > 0) {
        setTimeout(() => {
          removeToast(id);
        }, duration);
      }

      return id;
    },
    []
  );

  // Remove a toast by id
  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  // Shorthand methods
  const showSuccess = useCallback(
    (message, duration) => {
      return addToast(message, TOAST_TYPES.SUCCESS, duration);
    },
    [addToast]
  );

  const showError = useCallback(
    (message, duration = 6000) => {
      return addToast(message, TOAST_TYPES.ERROR, duration);
    },
    [addToast]
  );

  const showWarning = useCallback(
    (message, duration) => {
      return addToast(message, TOAST_TYPES.WARNING, duration);
    },
    [addToast]
  );

  const showInfo = useCallback(
    (message, duration) => {
      return addToast(message, TOAST_TYPES.INFO, duration);
    },
    [addToast]
  );

  // Clear all toasts
  const clearAll = useCallback(() => {
    setToasts([]);
  }, []);

  const value = {
    toasts,
    addToast,
    removeToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    clearAll,
  };

  return (
    <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
  );
}

/**
 * Hook to access the toast context
 * @returns {Object} Toast context with methods: showSuccess, showError, showWarning, showInfo, clearAll
 *
 * @example
 * const { showSuccess, showError } = useToast();
 *
 * // Show success toast
 * showSuccess("ההגדרות נשמרו בהצלחה");
 *
 * // Show error toast (longer duration by default)
 * showError("שגיאה בשמירת ההגדרות");
 */
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return context;
}

export default ToastContext;
